import { pageUrls } from 'globalconfig/constants/keys';
import { BACKEND_DATE_FORMAT } from 'globalconfig/constants/static';
import { getListOfSelectedField, hasEnabledFeature, hasValue } from 'globalutils/fmUtils';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { lazy, useEffect, useState, useMemo } from 'react';
import { object } from 'yup';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';
import RenderComponent from './RenderComponent';
import useInitialValues from './useInitialValues';
import useSchema from './useSchema';
import { trackingDeviceList } from './config';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Form = lazy(() => import(`globalcomponents/UI/Form/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Modal = require(`globalcomponents/UI/Modal/${layout}`).default;
const SvgIcon = lazy(() => import(`globalcomponents/SvgIcon/${layout}`));

const VehicleForm = ({
  apiStatus,
  saveVehicleData,
  getLatLongAndSave,
  vehicle,
  history,
  saveDocument,
  deleteDocument,
  callback,
  fetchTerminals,
  fetchDepartments,
  fetchSubDepartments,
  regions,
  type,
  states,
  radiusCompliances,
  i18n,
  isValidVin,
  fetchCountData,
  downloadDocument,
  logBook,
  terminals,
  departments,
  subDepartments,
  disabledCriteria,
  fleetCompanies,
  Styled,
  receipt,
  newUI,
  getRegions,
  recallUpdate,
}) => {
  const SERVICE_PROVIDER = window.$environment?.SERVICE_PROVIDER;
  const [forms, setForms] = useState([]);
  const [isCameraEnabled, setCameraEnabled] = useState(false);

  useEffect(() => {
    const url = receipt ? receipt : '';
    document.getElementById(`downloadDocument`).src = url;
  }, [receipt]);

  const apiId = type + 'SaveVehicleData';
  const latLongApiId = type + 'fetchLatLong';
  const hasApiStatus = hasValue(apiStatus.status, 'api', apiId);

  /* Create a popup starts */
  const [ModalListObject, setModalListObject] = useState({
    header: '',
    content: () => {},
  });
  const [open, setOpen] = useState(false);

  /* Message modal for info/warning toasts (e.g. Geometris camera messages) */
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalData, setMessageModalData] = useState({ title: '', message: '' });
  const showMessageModal = (title, message) => {
    setMessageModalData({ title, message });
    setMessageModalOpen(true);
  };

  /* Confirm modal for "switch camera type away from Geometris" - change applied only on OK; close icon cancels */
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({ title: '', message: '', onConfirm: null });
  const showConfirmCameraTypeChangeModal = (title, message, onConfirm) => {
    setConfirmModalData({ title, message, onConfirm });
    setConfirmModalOpen(true);
  };
  const handleConfirmOk = () => {
    if (confirmModalData.onConfirm) confirmModalData.onConfirm();
    setConfirmModalOpen(false);
  };
  const handleConfirmClose = () => {
    setConfirmModalOpen(false);
    setConfirmModalData({ title: '', message: '', onConfirm: null });
  };

  const objectArrayFromSameField = (list) => {
    return list?.length
      ? list.map((item) => {
          return {
            label: item,
            value: item,
          };
        })
      : [];
  };

  const objectArrayFromDiffFields = (list, value, field1, field2, callback) => {
    return list?.length
      ? list.map((item) => {
          if (callback) return callback(item, field1, field2, value);
          else
            return {
              label: field2 ? `${item[field1]} ${item[field2]}` : item[field1],
              value: item[value],
            };
        })
      : [];
  };

  const { vehicleTypes, cameraTypes, fuelTypes, years, driverList, mechanicList, trailerList, hardwareDeviceList, companies } =
    useMemo(() => {
      const vehicleTypes = objectArrayFromSameField(vehicle.vehicleTypes);
      const cameraTypes = objectArrayFromSameField([...(vehicle.cameraTypes || [])]);
      const fuelTypes = objectArrayFromSameField(vehicle.fuelTypes);
      const years = objectArrayFromSameField(vehicle.years);
      const driverList = objectArrayFromDiffFields(vehicle?.drivers, 'id', 'firstName', 'lastName');
      const mechanicList = objectArrayFromDiffFields(vehicle?.mechanics, 'id', 'firstName', 'lastName');
      const trailerList = objectArrayFromDiffFields(vehicle?.companyTrailers, 'trailerId', 'name');
      const hardwareDeviceList = objectArrayFromDiffFields(vehicle?.hardwareDevices, 'deviceKey', 'name');
      const companies = objectArrayFromDiffFields(fleetCompanies, 'id', 'name');

      return {
        vehicleTypes,
        cameraTypes,
        fuelTypes,
        years,
        driverList,
        mechanicList,
        trailerList,
        hardwareDeviceList,
        companies,
      };
    }, [vehicle, fleetCompanies]);

  const checkCameraFeature = () => {
    if (checkServiceProvider('form_checkCameraFeature', SERVICE_PROVIDER, false)) {
      return vehicle?.cameraIntegrationEnabled;
    }
    return vehicle?.cameraIntegrationEnabled || hasEnabledFeature('cameraIntegrationEnabled');
  };

  const { schema: validationlist } = useSchema({ checkCameraFeature });

  const { initialValues } = useInitialValues({
    vehicle,
    regions,
    type,
    driverList,
    hardwareDeviceList,
    mechanicList,
    vehicleTypes,
    fuelTypes,
    cameraTypes,
    trailerList,
    years,
    checkCameraFeature,
    objectArrayFromDiffFields,
  });

  useEffect(() => {
    const objectArrayFromDiffFields = (list, field, value) => {
      const lt = list?.length
        ? list.map((item) => {
            return {
              label: item[value],
              value: item[field],
            };
          })
        : [];
      return lt;
    };

    const inspectionForms = objectArrayFromDiffFields(
      vehicle?.customDvirForms?.filter((ele) => ele.unitType == 'VEHICLE'),
      'idPub',
      'name'
    );

    setForms(inspectionForms);
  }, [vehicle]);

  useEffect(() => {
    setCameraEnabled(vehicle.cameraIntegrationEnabled);
  }, [vehicle.cameraIntegrationEnabled]);

  const getPayload = (fields) => {
    return {
      wlId: fields.wlId || null,
      companyId: fields.company?.value || null,
      departmentId: (fields.departmentId && fields.departmentId.value) || null,
      deviceType: fields.deviceType || 'NONE',
      esn: fields.esn,
      gvw: fields.gvw,
      fuelType: fields.fuelType?.value || null,
      ibeaconRequired: fields.ibeaconRequired || null,
      ibeaconUuid: fields.ibeaconUuid || null,
      idPub: fields.idPub,
      isEdit: fields.isEdit,
      ...checkServiceProvider('form_payload', SERVICE_PROVIDER, false, { fields }),
      latitude: fields.latitude,
      longitude: fields.longitude,
      garagingAddress: fields.garagingAddress,
      garagingCity: fields.garagingCity,
      garagingState: fields.garagingState?.value,
      garagingZipCode: fields.garagingZipCode,
      radiusCompliance: fields.radiusCompliance?.value,
      lienholder: fields.notes,
      make: fields.make,
      mechanicId: fields.mechanicId?.value || null,
      model: fields.model || null,
      name: fields.name || null,
      nextMaintenanceMileage: fields.nextMaintenanceMileage ? parseFloat(fields.nextMaintenanceMileage) : null,
      odometer: fields.currentOdometer ? parseInt(fields.currentOdometer) : null,
      ...(checkCameraFeature()
        ? {
            cameraType: fields.camera == 'Yes' && fields.cameraType ? fields.cameraType?.value : null,
            cameraSerialNo: fields.camera == 'Yes' && fields.cameraSerialNo ? fields.cameraSerialNo : null,
            cameraPassword: fields.camera == 'Yes' && fields.cameraPassword ? fields.cameraPassword : null,
          }
        : {}),
      plateNumber: fields.plateNumber,
      regionId: (fields.regionId && fields.regionId.value) || null,
      terminalId: (fields.terminalId && fields.terminalId.value) || null,
      subDepartmentId: (fields.subDepartmentId && fields.subDepartmentId.value) || null,
      type: fields.type?.value || null,
      vehicleState: fields.vehicleState || null,
      vin: fields.vin,
      year: fields.year?.value || null,
      customDvirFormId: fields.customDvirFormId?.value || null,
      next_maintenance_date: moment(fields?.next_maintenance_date).isValid()
        ? moment(fields.next_maintenance_date).format(BACKEND_DATE_FORMAT)
        : '',
      ...(fields.deviceType === 'ELD' && {
        hardwareDeviceKey: fields.hardwareDeviceKey?.value || null,
      }),
      ...(fields.deviceType === 'Tracking' && {
        trackingDevice: fields.trackingDevice?.value || null,
      }),
      trailerIdPubs: getListOfSelectedField(fields.trailerIdPubs, 'value') || null,
      driversIds: getListOfSelectedField(fields.driversIds, 'value') || null,
    };
  };

  const handleCallback = (cameraError, errorMsg, fromCatch) => {
    if (fromCatch) {
      callback('error', errorMsg);
    } else {
      if (cameraError) {
        if (Array.isArray(errorMsg) && errorMsg?.length > 1) {
          callback(
            'warning',
            ' ',
            () => {
              history.push(pageUrls.vehicleList);
            },
            errorMsg
          );
        } else {
          callback(
            'warning',
            errorMsg?.[0],
            () => {
              history.push(pageUrls.vehicleList);
            },
            []
          );
        }
      } else {
        let apiId = 'fetchCountApi';
        history.push(pageUrls.vehicleList);
        fetchCountData({
          apiId,
        });
      }
    }
  };

  const triggerLatLong = (fields) => {
    getLatLongAndSave({
      data: {
        data: {
          ...getPayload(fields),
        },
      },
      address: `${fields.garagingAddress || ''},${fields.garagingCity || ''},${fields.garagingState.value || ''},${
        fields.garagingZipCode || ''
      }`,
      apiId,
      latLongApiId,
      i18n,
      latLongCallback: callback,
      callback: (cameraError, errorMsg, fromCatch) => {
        handleCallback(cameraError, errorMsg, fromCatch);
      },
    });
  };

  const triggerVehicle = (fields) => {
    saveVehicleData({
      data: {
        data: {
          ...getPayload(fields),
        },
      },
      apiId,
      callback: (cameraError, errorMsg, fromCatch) => {
        handleCallback(cameraError, errorMsg, fromCatch);
      },
    });
  };

  const submitVehicleData = (fields) => {
    if (checkServiceProvider('form_triggerLatLong', SERVICE_PROVIDER, false)) {
      triggerLatLong(fields);
    } else {
      triggerVehicle(fields);
    }
  };

  return (
    <>
      <Form
        type="server"
        direction="column"
        initialValues={initialValues}
        RenderComponent={RenderComponent}
        handleOnSubmit={(fields) => {
          fields.isEdit
            ? submitVehicleData(fields)
            : setModalListObject({
                content: () => (
                  <div className="deleteParent">
                    <div className="imageSection">
                      <SvgIcon value={mediaUrls.confirmIcon} alt="Are you sure" />
                    </div>
                    <h2>
                      {checkServiceProvider('form_modalhead', SERVICE_PROVIDER, false, { totalTrucks: vehicle?.totalTrucks + 1 })}
                    </h2>
                    <div className="deletePopupBtn">
                      <div className="cancelBtn">
                        <Button
                          label={checkServiceProvider('form_btn_label_secodary', SERVICE_PROVIDER, false)}
                          type={'reset'}
                          onClick={() => {
                            setOpen(false);
                          }}
                        ></Button>
                      </div>
                      <div className="deletebtn">
                        <Button
                          label={checkServiceProvider('form_btn_label_primary', SERVICE_PROVIDER, false)}
                          onClick={() => {
                            submitVehicleData(fields);
                            setOpen(false);
                          }}
                        ></Button>
                      </div>
                    </div>
                  </div>
                ),
              });
          !fields.isEdit && setOpen(true);
        }}
        isCameraEnabled={isCameraEnabled}
        customProps={{
          forms,
          hasApiStatus,
          saveDocument,
          deleteDocument,
          apiStatus,
          callback,
          vehicleTypes,
          fuelTypes,
          fetchTerminals,
          fetchDepartments,
          fetchSubDepartments,
          setModalListObject,
          setOpen,
          setValidationlist: () => {},
          trackingDeviceList,
          validationlist,
          years,
          driverList,
          mechanicList,
          trailerList,
          hardwareDeviceList,
          vehicle,
          regions,
          type,
          isValidVin,
          i18n,
          downloadDocument,
          logBook,
          terminals,
          companies,
          departments,
          radiusCompliances,
          states,
          subDepartments,
          disabledCriteria,
          setInitialValues: () => {},
          documentsList: vehicle?.documents,
          Styled,
          newUI,
          getRegions,
          recallUpdate,
          cameraTypes,
          showMessageModal,
          showConfirmCameraTypeChangeModal,
        }}
        handleOnCancel={() => {
          history.push(pageUrls.vehicleList);
        }}
        validationSchema={object().shape({
          ...validationlist,
        })}
      />
      <Modal open={open} setOpen={setOpen} data={ModalListObject} deletePopup />
      <Modal
        open={messageModalOpen}
        setOpen={setMessageModalOpen}
        data={{
          header: messageModalData.title,
          content: () => (
            <div className="messageModalContent">
              <p style={{ margin: '0 0 16px', whiteSpace: 'pre-wrap' }}>{messageModalData.message}</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button label={i18n.t('common.ok') || 'OK'} type="button" onClick={() => setMessageModalOpen(false)} />
              </div>
            </div>
          ),
        }}
      />
      <Modal
        open={confirmModalOpen}
        setOpen={handleConfirmClose}
        withCloseIconHeader
        data={{
          header: confirmModalData.title,
          content: () => (
            <div className="confirmCameraTypeModalContent">
              <p style={{ margin: '0 0 16px', whiteSpace: 'pre-wrap' }}>{confirmModalData.message}</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  label={i18n.t('common.ok') || 'OK'}
                  type="button"
                  onClick={handleConfirmOk}
                />
              </div>
            </div>
          ),
        }}
      />
      <iframe id="downloadDocument" title="downloadDocument" style={{ display: 'none' }}></iframe>
    </>
  );
};

VehicleForm.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
};

export default VehicleForm;
